// Per-tick simulation pipeline. See docs/05-combat-sim §5.5.
import { TICKS_PER_SECOND, } from '@renavora/shared';
import { evaluateActivation } from '../activation.js';
import { tickDamageReactRules } from '../ai/damage-react.js';
import { decideAction } from '../ai/decide.js';
import { getSkill } from '../content/skills.js';
import { calculateDamage } from '../damage.js';
import { resolveDamageElement } from '../element-resolver.js';
import { applyEffects, fireDamageReactiveEvents, fireOnCritHooks, revertStatBoostBuff, } from '../effects.js';
import { AUTO_ATTACK_KEY, buildCandidates, isSkillInRange, nextAutoAttackTick, } from './candidates.js';
import { applyCooldownRateMod } from './cd-clamp.js';
import { getAxis6CdrLayer } from './cd-layers.js';
import { tickMarkDecay } from './marks.js';
import { initPeriodicSkills, tickPeriodicSkills } from './periodic-skills.js';
import { projectileShieldMissChanceBonus, tickPlaceables, triggerManualPlaceables, } from './placeables.js';
import { fireReactiveSkills } from './reactive-events.js';
import { drainDueEffects } from './scheduling.js';
import { getTimeLimit } from './state.js';
import { tickSummons } from './summons.js';
import { applyDamageWithHooks, tickUltMechanics } from './ult-mechanics.js';
/** Default attack range used by the auto-attack fallback. */
const AUTO_ATTACK_RANGE = 5;
/** Maximum movement per tick = moveSpeed (units/sec) / TICKS_PER_SECOND. */
function maxMoveDistance(entity) {
    const base = entity.stats.moveSpeed;
    const mod = 1 + entity.speedMods.actionSpeed + entity.speedMods.moveSpeed;
    return (base * Math.max(0.2, mod)) / TICKS_PER_SECOND;
}
/** Move `entity.pos` toward `desired`, capped at the per-tick step distance. */
function stepTowards(entity, desired) {
    const dx = desired.x - entity.pos.x;
    const dy = desired.y - entity.pos.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist <= 1e-6)
        return;
    const step = maxMoveDistance(entity);
    if (step >= dist) {
        entity.pos = { x: desired.x, y: desired.y };
        return;
    }
    entity.pos = {
        x: entity.pos.x + (dx / dist) * step,
        y: entity.pos.y + (dy / dist) * step,
    };
}
function distance(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
}
function makeView(state) {
    return {
        tick: state.tick,
        entities: state.entities,
        recentAttackers: state.recentAttackers,
        damageReactOverrides: state.damageReactOverrides,
    };
}
function findEntity(state, id) {
    return state.entities.find((e) => e.id === id);
}
/** Decay buff / debuff durations and drop expired entries. */
function maintainBuffs(entity, tick) {
    const events = [];
    const drop = (list) => {
        const remaining = [];
        for (const b of list) {
            if (b.expiresAt <= tick) {
                revertStatBoostBuff(entity, b);
                events.push({ kind: 'statusExpired', tick, targetId: entity.id, statusId: b.id });
            }
            else {
                remaining.push(b);
            }
        }
        return remaining;
    };
    entity.buffs = drop(entity.buffs);
    entity.debuffs = drop(entity.debuffs);
    return events;
}
/**
 * Check HP threshold crossings and fire `onHpBelow` / `onAllyHpBelow` reactive
 * skills exactly once per crossing. ADR 0014 §2.4 / §8.1。
 *
 * 規約:
 *   - 各 reactive Skill について HP fraction が threshold 以下になった瞬間 1 回
 *     発火 (`reactiveHpFlags` に id 追加)
 *   - HP fraction が threshold より上に回復したら flag 解除 → 再度割り込みで再発火可
 *   - **threshold の source**:
 *     - `onHpBelow` → `activation.selfHpAtMost` (caster 自身)
 *     - `onAllyHpBelow` → `activation.targetHpAtMost` (ally 側)。同 side ally の
 *       **最低 HP fraction** を見る (caster 自身は除外)
 *   - flag key は `onAllyHpBelow` 用に `<skillId>:ally` サフィックスで分離 (同一
 *     skill が両 event を持つ catalogue でも衝突しない契約)
 *
 *   `selfHpAtMost` は `evaluateActivation` で caster の自身 HP も別途 PASS が必要
 *   (本関数で閾値跨ぎ判定済でも evaluateActivation で同条件を再評価)。
 *   `targetHpAtMost` は `evaluateActivation` で primaryTarget の HP が threshold
 *   以下である PASS が必要 (ally の HP が落ちている前提)。
 */
function checkReactiveHpThresholds(state) {
    for (const entity of state.entities) {
        if (entity.hp <= 0)
            continue;
        const flags = ensureHpFlagSet(state, entity.id);
        const selfFrac = entity.hp / Math.max(entity.hpMax, 1);
        // Find lowest-fraction ally once per entity (excluding self).
        let lowestAlly = null;
        let lowestAllyFrac = Number.POSITIVE_INFINITY;
        for (const ally of state.entities) {
            if (ally.id === entity.id)
                continue;
            if (ally.side !== entity.side || ally.hp <= 0)
                continue;
            const f = ally.hp / Math.max(ally.hpMax, 1);
            if (f < lowestAllyFrac) {
                lowestAllyFrac = f;
                lowestAlly = ally;
            }
        }
        for (const itemSkill of entity.itemSkills) {
            const skill = getSkill(itemSkill.templateId);
            if (skill === undefined)
                continue;
            if (skill.activation.onEvent === 'onHpBelow') {
                const threshold = skill.activation.selfHpAtMost;
                if (threshold === undefined)
                    continue;
                if (selfFrac <= threshold && !flags.has(skill.id)) {
                    flags.add(skill.id);
                    fireReactiveSkills(state, entity.id, 'onHpBelow', null);
                }
                else if (selfFrac > threshold && flags.has(skill.id)) {
                    flags.delete(skill.id);
                }
            }
            else if (skill.activation.onEvent === 'onAllyHpBelow') {
                const threshold = skill.activation.targetHpAtMost;
                if (threshold === undefined)
                    continue;
                const flagKey = `${skill.id}:ally`;
                if (lowestAlly !== null && lowestAllyFrac <= threshold && !flags.has(flagKey)) {
                    flags.add(flagKey);
                    fireReactiveSkills(state, entity.id, 'onAllyHpBelow', lowestAlly);
                }
                else if ((lowestAlly === null || lowestAllyFrac > threshold) && flags.has(flagKey)) {
                    flags.delete(flagKey);
                }
            }
        }
    }
}
function ensureHpFlagSet(state, id) {
    let s = state.reactiveHpFlags.get(id);
    if (s === undefined) {
        s = new Set();
        state.reactiveHpFlags.set(id, s);
    }
    return s;
}
/** Drop expired shield layers. */
function maintainShields(entity, tick) {
    if (entity.shields.length === 0)
        return;
    let writeIdx = 0;
    for (let readIdx = 0; readIdx < entity.shields.length; readIdx++) {
        const layer = entity.shields[readIdx];
        if (layer.expiresAt > tick && layer.amount > 0) {
            entity.shields[writeIdx++] = layer;
        }
    }
    entity.shields.length = writeIdx;
}
/** Resolve one auto-attack from `attacker` to `target`. Mutates state. */
function resolveAutoAttack(attacker, target, state, tick) {
    const element = resolveDamageElement('weapon-derived', attacker);
    const input = {
        element,
        power: 1,
        missChanceBonus: projectileShieldMissChanceBonus(target, state),
    };
    const result = calculateDamage(attacker, target, input, state.rng);
    if (!result.hit) {
        state.events.push({
            kind: 'damage',
            tick,
            sourceId: attacker.id,
            targetId: target.id,
            element: input.element,
            amount: 0,
            crit: false,
        });
    }
    else {
        const shieldsBefore = target.shields.length > 0;
        const apply = applyDamageWithHooks(attacker, target, result.finalDamage, state);
        const shieldBrokeThisHit = shieldsBefore && target.shields.length === 0 && apply.absorbedByShield > 0;
        state.events.push({
            kind: 'damage',
            tick,
            sourceId: attacker.id,
            targetId: target.id,
            element: input.element,
            amount: apply.damageApplied,
            crit: result.crit,
        });
        state.recentAttackers.set(target.id, attacker.id);
        if (apply.killed) {
            state.events.push({ kind: 'death', tick, targetId: target.id });
        }
        // ADR 0014 §2.1 / §2.4 / §2.6: reactive event 発火 (auto-attack 経路)。
        // ADR 0014 §2.4 §onSummonAttack: 召喚体の auto-attack はここで `onSummonAttack`
        // (1 召喚体 1 tick 1 回集約) を発火。`summonAttackedThisTick` で重複防止。
        fireDamageReactiveEvents(attacker, target, { ...apply, crit: result.crit, hit: result.hit }, state, shieldBrokeThisHit);
        if (attacker.isPet === true && !state.summonAttackedThisTick.has(attacker.id)) {
            const inst = state.summonByEntity.get(attacker.id);
            if (inst !== undefined) {
                state.summonAttackedThisTick.add(attacker.id);
                // ADR 0014 §2.4: 1 召喚体 1 tick 1 回集約 (summonAttackedThisTick で重複防止)。
                // owner 側で onSummonAttack reactive を発火 (primaryTarget = summon の攻撃対象)。
                fireReactiveSkills(state, inst.ownerId, 'onSummonAttack', target);
            }
        }
        // ADR 0009 §2.8: auto-attack crit fires onCritEffects on the attacker.
        if (result.crit)
            fireOnCritHooks(attacker, target, state, AUTO_ATTACK_KEY);
    }
    attacker.cooldowns.set(AUTO_ATTACK_KEY, nextAutoAttackTick(attacker, tick));
}
/**
 * Advance battle state by one tick. Mutates `state` and returns it.
 * Idempotent if `state.outcome !== null`.
 *
 * Step ordering (ADR 0009 §2.11):
 *   0. drainDueEffects (delayed effects fire before anything observes them)
 *   1. maintainBuffs / maintainShields (decay)
 *   1.5 tickPlaceables / tickSummons (lifecycle + trigger / aura refresh)
 *   2. AI decisions
 *   3. Movement
 *   4. Combat (skill cast w/ hpCost gate, auto-attack fallback)
 *   5. End condition adjudication
 */
export function step(state) {
    if (state.outcome !== null)
        return state;
    state.tick += 1;
    const tick = state.tick;
    // ADR 0014 §2.1 (機構 3) / 軸 6 §6.0: battle init 相当の passive 適用は
    // 初回 step (tick === 1) のタイミングで実施。initBattle を直接修正すると
    // state.ts → periodic-skills.ts → effects.ts → state.ts の循環参照が
    // 発生するため、step 経由で lazy init する。
    if (tick === 1) {
        initPeriodicSkills(state);
    }
    // ADR 0014 §2.4: tick 開始時に 1 tick 集約用 set/map をクリア。
    state.firedReactiveThisTick.clear();
    state.summonAttackedThisTick.clear();
    // 0. Drain due scheduled effects first so the rest of this tick observes them.
    drainDueEffects(state);
    // ADR 0014 §2.3.3 / 軸 1 §A.2: mark stack 個別 expire を maintainBuffs の前に処理。
    // 全 stack 個別 expire 済の BuffStack は expiresAt = tick に詰められ、後続の
    // maintainBuffs で drop される (二重実装回避)。
    tickMarkDecay(state);
    // 1. State maintenance: cooldowns, buffs, shields.
    for (const entity of state.entities) {
        if (entity.hp <= 0)
            continue;
        state.events.push(...maintainBuffs(entity, tick));
        maintainShields(entity, tick);
    }
    // ADR 0014 §2.4 / §8.1: maintenance 後に HP 閾値 crossing 判定。
    // `onHpBelow` / `onAllyHpBelow` reactive Skill を閾値跨ぎで 1 回発火。
    checkReactiveHpThresholds(state);
    // 1.5. Placeable / summon lifecycle ticks. Empty arrays no-op.
    tickPlaceables(state);
    tickSummons(state);
    // 1.6. ADR 0014 §2.1 (機構 3) / 軸 6 §6.0: passive 周期発火。
    tickPeriodicSkills(state);
    // 1.7. Ult-mechanic instance maintenance (counterStance / store pools /
    //      sharedAbsorbPool / phaseCyclingAura / anchoredHealHook /
    //      damageConversion). Empty arrays no-op. ADR 0010 §2.11.
    tickUltMechanics(state);
    // 1.8. DamageReact stat trigger evaluation + effective threatMod refresh.
    tickDamageReactRules(state);
    const decisions = new Map();
    const view = makeView(state);
    for (const entity of state.entities) {
        if (entity.hp <= 0)
            continue;
        // First pass at AI to get the target so we can build candidates with
        // accurate range checks, then a second pass with the candidate list.
        const provisional = decideAction(entity, view, state.rng);
        const candidates = buildCandidates(entity, state, provisional.targetId);
        const decision = decideAction(entity, view, state.rng, { candidates });
        decisions.set(entity.id, decision);
    }
    // 3. Movement: step each entity toward its desired position.
    for (const entity of state.entities) {
        if (entity.hp <= 0)
            continue;
        const decision = decisions.get(entity.id);
        if (!decision)
            continue;
        stepTowards(entity, decision.desiredPos);
    }
    // 4. Combat resolution: skill cast (preferred) or auto-attack fallback.
    for (const entity of state.entities) {
        if (entity.hp <= 0)
            continue;
        const decision = decisions.get(entity.id);
        if (!decision || decision.targetId === null)
            continue;
        const target = findEntity(state, decision.targetId);
        if (!target || target.hp <= 0)
            continue;
        if (decision.castSkillId !== null) {
            const skill = getSkill(decision.castSkillId);
            // ADR 0014 §2.1: AI の active firing path は `kind === 'active'` のみ受け付ける。
            // trigger / reactive / passive は別経路 (reactive-events / periodic-skills) で発火。
            // 候補生成側でも active のみへ絞るが、最終ゲートとしてここでも kind ガードする。
            if (skill && skill.kind === 'active') {
                const cdReady = (entity.cooldowns.get(skill.id) ?? 0) <= tick;
                const rangeReady = isSkillInRange(skill, entity, target, state);
                // ADR 0009 §2.5: hpCost is a firing-eligibility gate (alongside CD).
                // Strict `>` so the cast cannot kill the caster while resolving.
                const hpReady = skill.hpCost === undefined || entity.hp > skill.hpCost;
                // ADR 0014 §2.1 / §5.1: Activation evaluator gate (9 値 + jobTypeBonus).
                // AI 側 (candidates.ts) は無修正で、本判定が最終ゲートとなる。
                // ADR 0015 §4.8: 戻り値の effectAmountMod を applyEffects に伝播。
                const act = evaluateActivation(skill, entity, target, state);
                if (cdReady && rangeReady && hpReady && act.canFire) {
                    if (skill.hpCost !== undefined) {
                        // Pay the cost up-front, before effects fire (§2.5).
                        entity.hp -= skill.hpCost;
                    }
                    state.events.push({
                        kind: 'skillCast',
                        tick,
                        sourceId: entity.id,
                        skillId: skill.id,
                        targetId: target.id,
                    });
                    // ADR 0014 §2.5 / ADR 0015 §4.3: cooldown は CD 30% クランプ経由で設定。
                    // axis6 layer は cd-layers.ts の getAxis6CdrLayer から取得。
                    const baseCd = skill.cooldownTicks ?? 1;
                    const effectiveCd = applyCooldownRateMod(baseCd, {
                        affix: 0,
                        axis6: getAxis6CdrLayer(entity),
                        other: Math.max(0, entity.speedMods.cooldownRate),
                    });
                    entity.cooldowns.set(skill.id, tick + effectiveCd);
                    // ADR 0015 §4.8: effectAmountMod は damage/heal/shield の power のみ乗算。
                    // P55-024: consumeNextCast で積まれた pendingCastBoost を 1 回だけ
                    // 折り込み、消費する (exactOptionalPropertyTypes 安全に delete)。
                    let castEffectMod = act.effectAmountMod;
                    if (entity.pendingCastBoost !== undefined) {
                        castEffectMod *= entity.pendingCastBoost;
                        delete entity.pendingCastBoost;
                    }
                    const sourceItemSkill = entity.itemSkills.find((it) => it.templateId === skill.id);
                    applyEffects(skill, entity, target, state, {
                        effectAmountMod: castEffectMod,
                        ...(sourceItemSkill !== undefined ? { sourceItemSkill } : {}),
                    });
                    triggerManualPlaceables(state, entity, skill.id);
                    continue;
                }
            }
        }
        // Auto-attack fallback when no skill fires.
        const nextAuto = entity.cooldowns.get(AUTO_ATTACK_KEY) ?? 0;
        if (tick < nextAuto)
            continue;
        if (distance(entity.pos, target.pos) > AUTO_ATTACK_RANGE + entity.radius + target.radius) {
            continue;
        }
        resolveAutoAttack(entity, target, state, tick);
    }
    // 5. End condition.
    adjudicateEndCondition(state);
    return state;
}
/** Apply victory / timeout adjudication for the state's current tick. */
export function adjudicateEndCondition(state) {
    if (state.outcome !== null)
        return;
    const limit = getTimeLimit(state.config);
    const allies = state.entities.filter((e) => e.side === 'ally');
    const enemies = state.entities.filter((e) => e.side === 'enemy');
    const allyAlive = allies.some((e) => e.hp > 0);
    const enemyAlive = enemies.some((e) => e.hp > 0);
    if (!allyAlive && !enemyAlive) {
        state.outcome = 'draw';
    }
    else if (!enemyAlive) {
        state.outcome = 'allyWin';
    }
    else if (!allyAlive) {
        state.outcome = 'enemyWin';
    }
    else if (state.tick >= limit) {
        const hpFraction = (group) => {
            let cur = 0;
            let max = 0;
            for (const e of group) {
                cur += Math.max(0, e.hp);
                max += e.hpMax;
            }
            return max === 0 ? 0 : cur / max;
        };
        const allyFrac = hpFraction(allies);
        const enemyFrac = hpFraction(enemies);
        if (Math.abs(allyFrac - enemyFrac) < 1e-9) {
            state.outcome = 'draw';
        }
        else if (enemyFrac < state.config.timeoutHpThreshold && allyFrac >= enemyFrac) {
            state.outcome = 'timeoutAlly';
        }
        else if (allyFrac < state.config.timeoutHpThreshold && enemyFrac >= allyFrac) {
            state.outcome = 'timeoutEnemy';
        }
        else {
            state.outcome = allyFrac >= enemyFrac ? 'timeoutAlly' : 'timeoutEnemy';
        }
    }
}
export function isFinished(state) {
    return state.outcome !== null;
}
//# sourceMappingURL=tick.js.map